import { Controller } from "@hotwired/stimulus";

/**
 * Uses event delegation to handle conditional visibility for checkbox/radio inputs within forms.
 *
 * Add data attributes to inputs to control conditional visibility:
 *
 * When input becomes checked:
 * - data-show-on-checked="selector" - shows matching elements
 * - data-hide-on-checked="selector" - hides matching elements
 * - data-set-on-checked="selector" - checks matching inputs
 * - data-unset-on-checked="selector" - unchecks matching inputs
 * - data-require-on-checked="selector" - makes matching inputs required
 * - data-unrequire-on-checked="selector" - removes required from matching inputs
 *
 * When input becomes unchecked:
 * - data-show-on-unchecked="selector" - shows matching elements
 * - data-hide-on-unchecked="selector" - hides matching elements
 * - data-set-on-unchecked="selector" - checks matching inputs
 * - data-unset-on-unchecked="selector" - unchecks matching inputs
 * - data-require-on-unchecked="selector" - makes matching inputs required
 * - data-unrequire-on-unchecked="selector" - removes required from matching inputs
 *
 * Values:
 * - data-form-input-toggle-required-class-value="ClassName" - CSS class to add/remove for required fields (default: "FormField--Required")
 *
 * Usage:
 *   <form data-controller="form-input-toggle" data-form-input-toggle-required-class-value="CustomRequiredClass">
 *     <input type="radio" data-show-on-checked="#target" />
 *     <div id="target" class="hidden">Content to show when radio is checked</div>
 *   </form>
 */
export default class extends Controller<HTMLFormElement> {
  static values = {
    requiredClass: String,
  };

  declare readonly hasRequiredClassValue: boolean;
  declare requiredClassValue: string;
  connect() {
    // Apply initial state to all inputs with toggle data attributes
    this.initializeAllInputs();

    // Listen for changes on any input within this form
    this.element.addEventListener("change", this.handleChange);
  }

  disconnect() {
    this.element.removeEventListener("change", this.handleChange);
  }

  private handleChange = (event: Event): void => {
    const target = event.target;
    if (
      target instanceof HTMLInputElement &&
      (target.type === "checkbox" || target.type === "radio")
    ) {
      this.toggleInput(target);
    }
  };

  private initializeAllInputs(): void {
    const inputs = this.element.querySelectorAll<HTMLInputElement>(
      "input[type=checkbox], input[type=radio]",
    );
    inputs.forEach((input) => {
      if (this.hasToggleAttributes(input)) {
        this.toggleInput(input);
      }
    });
  }

  private toggleInput(input: HTMLInputElement): void {
    // For radio buttons, handle group behavior - process ALL radios in group
    if (input.type === "radio" && input.form) {
      const radiosInGroup = this.getRadioGroupElements(input);
      for (const radio of radiosInGroup) {
        // Apply toggle state to ALL radios in group
        this.applyToggleState(radio);
      }
    } else {
      // For checkboxes, just apply the toggle state
      this.applyToggleState(input);
    }
  }

  private applyToggleState(input: HTMLInputElement): void {
    // Only apply toggle logic if input has toggle data attributes
    if (!this.hasToggleAttributes(input)) {
      return;
    }

    const isChecked = input.checked;
    const dataset = input.dataset;

    if (isChecked) {
      this.showElements(dataset.showOnChecked);
      this.hideElements(dataset.hideOnChecked);
      this.checkElements(dataset.setOnChecked);
      this.uncheckElements(dataset.unsetOnChecked);
      this.requireElements(dataset.requireOnChecked);
      this.unrequireElements(dataset.unrequireOnChecked);
    } else {
      this.showElements(dataset.showOnUnchecked);
      this.hideElements(dataset.hideOnUnchecked);
      this.checkElements(dataset.setOnUnchecked);
      this.uncheckElements(dataset.unsetOnUnchecked);
      this.requireElements(dataset.requireOnUnchecked);
      this.unrequireElements(dataset.unrequireOnUnchecked);
    }
  }

  private hasToggleAttributes(input: HTMLInputElement): boolean {
    const dataset = input.dataset;
    return !!(
      dataset.showOnChecked ||
      dataset.hideOnChecked ||
      dataset.setOnChecked ||
      dataset.unsetOnChecked ||
      dataset.requireOnChecked ||
      dataset.unrequireOnChecked ||
      dataset.showOnUnchecked ||
      dataset.hideOnUnchecked ||
      dataset.setOnUnchecked ||
      dataset.unsetOnUnchecked ||
      dataset.requireOnUnchecked ||
      dataset.unrequireOnUnchecked
    );
  }

  private showElements(selector?: string): void {
    if (selector) {
      this.querySelectorAllSafely(selector).forEach((element) => {
        element.classList.remove("hidden");
      });
    }
  }

  private hideElements(selector?: string): void {
    if (selector) {
      this.querySelectorAllSafely(selector).forEach((element) => {
        element.classList.add("hidden");
      });
    }
  }

  private checkElements(selector?: string): void {
    if (selector) {
      this.querySelectorAllSafely(selector).forEach((element) => {
        if (element instanceof HTMLInputElement && !element.checked) {
          element.checked = true;
          // Dispatch change event so other listeners stay in sync
          element.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });
    }
  }

  private uncheckElements(selector?: string): void {
    if (selector) {
      this.querySelectorAllSafely(selector).forEach((element) => {
        if (element instanceof HTMLInputElement && element.checked) {
          element.checked = false;
          // Dispatch change event so other listeners stay in sync
          element.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });
    }
  }

  private requireElements(selector?: string): void {
    if (selector) {
      this.querySelectorAllSafely(selector).forEach((element) => {
        if (element instanceof HTMLInputElement) {
          element.setAttribute("required", "true");
          if (this.hasRequiredClassValue) {
            element.closest(".FormField")?.classList.add(this.requiredClassValue);
          }
        }
      });
    }
  }

  private unrequireElements(selector?: string): void {
    if (selector) {
      this.querySelectorAllSafely(selector).forEach((element) => {
        if (element instanceof HTMLInputElement) {
          element.removeAttribute("required");
          if (this.hasRequiredClassValue) {
            element.closest(".FormField")?.classList.remove(this.requiredClassValue);
          }
        }
      });
    }
  }

  private querySelectorAllSafely(selector: string): HTMLElement[] {
    try {
      // Try form-scoped query first for performance and isolation
      const elements = Array.from(this.element.querySelectorAll<HTMLElement>(selector));

      // If no elements found in form, try document-wide for backwards compatibility
      if (elements.length === 0) {
        return Array.from(document.querySelectorAll<HTMLElement>(selector));
      }

      return elements;
    } catch (error) {
      console.warn(`FormInputToggleController: Invalid selector "${selector}"`, error);
      return [];
    }
  }

  private getRadioGroupElements(input: HTMLInputElement): HTMLInputElement[] {
    if (!input.form || !input.name) {
      return [input];
    }

    const elements = Array.from(input.form.elements);
    return elements.filter(
      (el): el is HTMLInputElement =>
        el instanceof HTMLInputElement && el.type === "radio" && el.name === input.name,
    );
  }
}
